import _ from 'lodash'
import { addDaysToDate, parseIntervalToDays } from '~/common/utils/date-utils'
import { TASK_TYPE_ICONS } from '~/crop/crop-schedule/hooks/use-timeline-styles'

/**
 * Task grouping strategies
 */
export const GROUP_STRATEGIES = {
  NONE: 'none',
  TYPE: 'type',       // Group by task_types.name
  TARGET: 'target',   // Group by org vs. crop tasks
}

/**
 * Task sorting strategies
 */
export const SORT_STRATEGIES = {
  DATE: 'date',           // Sort by task_window_start
  ALPHABETICAL: 'alpha',  // Sort by task_types.name
  STATE: 'state',         // Sort by task_states.code (ready → completed)
}

/**
 * Date offset strategies for filtering tasks
 */
export const DATE_OFFSET_STRATEGIES = {
  NOW: 'now',
  ONE_WEEK: '1w',
  TWO_WEEKS: '2w',
  ONE_MONTH: '1m',
  ALL: 'all'
}

export const applyScheduledState = (tasks, readyWindowOffsetDays) => {
  const now = new Date()
  let readyWindowEnd = new Date(now)
  readyWindowEnd.setDate(now.getDate() + readyWindowOffsetDays)
  
  // overwrite only the 'ready' states of tasks with task_window_start > readyWindowEnd
  // with 'scheduled'
  return tasks.map(task => {
    if (task.state !== 'ready') return task

    const taskWindowStart = new Date(task.task_window_start)

    const durationDays = parseIntervalToDays(task.duration)
    const taskWindowEnd = new Date(addDaysToDate(task.task_window_start, durationDays))

    return {
      ...task,
      ...( taskWindowStart > readyWindowEnd ? {
        state: 'scheduled',
        task_states: { ...task.task_states, name: 'Scheduled' }
      } : {} ),
      ...( now > taskWindowEnd ? {
        state: 'overdue',
        task_states: { ...task.task_states, name: 'Overdue' }
      } : {} )
    }
  })
}

/**
 * Group tasks based on strategy
 *
 * @param {Array} tasks - Array of task objects
 * @param {string} strategy - Grouping strategy (GROUP_STRATEGIES)
 * @returns {Array} Array of { id, label, tasks } objects
 */
export const groupTasks = (tasks, strategy) => {
  if (strategy === GROUP_STRATEGIES.NONE) {
    return [{ id: 'all', label: null, tasks }]
  }

  if (strategy === GROUP_STRATEGIES.TYPE) {
    const grouped = _.groupBy(tasks, t => t.task_types.name)
    return Object.entries(grouped).map(([name, groupedTasks]) => {
      const icon = TASK_TYPE_ICONS[groupedTasks[0]?.task_code] ?? '📌'
      return {
        id: name,
        label: `${icon} ${name}`,
        tasks: sortTasks(groupedTasks, SORT_STRATEGIES.DATE)
      }
    })
  }

  if (strategy === GROUP_STRATEGIES.TARGET) {
    const orgTasks = tasks.filter(t => t.crop_id === null)
    const cropTasks = tasks.filter(t => t.crop_id !== null)

    const orgGroups = Object.entries(_.groupBy(orgTasks, t => t.organization?.id ?? 'unknown'))
      .map(([orgId, groupTasks]) => ({
        id: `org-${orgId}`,
        label: `🏚️ ${groupTasks[0]?.organization?.name ?? 'Organization'}`,
        tasks: sortTasks(groupTasks, SORT_STRATEGIES.DATE)
      }))

    const cropGroups = Object.entries(_.groupBy(cropTasks, t => t.crop_id))
      .map(([cropId, groupTasks]) => ({
        id: `crop-${cropId}`,
        label: `🌾 ${groupTasks[0]?.crop?.name ?? 'Crop'}`,
        tasks: sortTasks(groupTasks, SORT_STRATEGIES.DATE)
      }))

    return [...orgGroups, ...cropGroups]
  }

  return []
}

/**
 * Sort tasks based on strategy
 *
 * @param {Array} tasks - Array of task objects
 * @param {string} strategy - Sorting strategy (SORT_STRATEGIES)
 * @returns {Array} Sorted array of task objects
 */
export const sortTasks = (tasks, strategy) => {
  const sorted = [...tasks]
  // Sort order: ready (actionable now) → scheduled (upcoming) → completed (done) → cancelled (stopped)
  const stateOrder = { overdue: 0, ready: 1, scheduled: 2, completed: 3, cancelled: 4 }

  switch (strategy) {
    case SORT_STRATEGIES.DATE:
      // Sort by state first (completed last), then by date within each state
      return sorted.sort((a, b) => {
        const stateCompare = (stateOrder[a.state] ?? 999) - (stateOrder[b.state] ?? 999)
        if (stateCompare !== 0) return stateCompare
        return new Date(a.task_window_start) - new Date(b.task_window_start)
      })

    case SORT_STRATEGIES.ALPHABETICAL:
      // Sort by state first (completed last), then alphabetically within each state
      return sorted.sort((a, b) => {
        const stateCompare = (stateOrder[a.state] ?? 999) - (stateOrder[b.state] ?? 999)
        if (stateCompare !== 0) return stateCompare
        return a.task_types.name.localeCompare(b.task_types.name)
      })

    case SORT_STRATEGIES.STATE:
      return sorted.sort((a, b) =>
        (stateOrder[a.state] ?? 999) - (stateOrder[b.state] ?? 999)
      )

    default:
      return sorted
  }
}

/**
 * Filter tasks by date offset from current date
 * Shows tasks up to and including the cutoff date
 *
 * @param {Array} tasks - Array of task objects
 * @param {string} offsetStrategy - Date offset strategy (DATE_OFFSET_STRATEGIES)
 * @returns {Array} Filtered array of task objects
 */
export const filterTasksByDateOffset = (tasks, offsetStrategy) => {
  if (offsetStrategy === DATE_OFFSET_STRATEGIES.ALL) {
    return tasks
  }

  const now = new Date()
  let cutoffDate = new Date(now)

  switch (offsetStrategy) {
    case DATE_OFFSET_STRATEGIES.NOW:
      cutoffDate.setDate(now.getDate())
      break
    case DATE_OFFSET_STRATEGIES.ONE_WEEK:
      cutoffDate.setDate(now.getDate() + 7)
      break
    case DATE_OFFSET_STRATEGIES.TWO_WEEKS:
      cutoffDate.setDate(now.getDate() + 14)
      break
    case DATE_OFFSET_STRATEGIES.ONE_MONTH:
      cutoffDate.setMonth(now.getMonth() + 1)
      break
    default:
      return tasks
  }

  return tasks.filter(task => {
    const taskDate = new Date(task.task_window_start)
    return taskDate <= cutoffDate
  })
}
